// Small shared helpers used across the app.

/** Zero-pad a 0-based index to a 2-digit label: 0 → "01", 9 → "10". */
export const pad2 = (i: number): string => String(i + 1).padStart(2, '0');

/** Smooth-scroll to an element by id. No-op if the element doesn't exist. */
export const scrollToId = (id: string): void => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

/** True when the user has requested reduced motion. */
export const prefersReducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Solid colours for the mobile browser chrome (iOS status bar / address bar).
// These match how the glass header reads at the top of the page — i.e. the
// header-glass blended over the hero background (--header-glass over --bg), so the
// chrome and header look continuous. Kept here AND in the pre-paint script in
// public/index.html — update both if these change.
const THEME_COLORS = {
  novaeon: { dark: '#0F0F18', light: '#F5F2EE' },
  playercafe: { dark: '#1a1c25', light: '#f8f1e9' },
} as const;

type Brand = keyof typeof THEME_COLORS;

export const currentBrand = (): Brand =>
  document.documentElement.getAttribute('data-brand') === 'playercafe' ? 'playercafe' : 'novaeon';

export const brandHomeName = (): string => currentBrand() === 'playercafe' ? 'PlayerCafe' : 'novaeon.io';

/**
 * Update the <meta name="theme-color"> so the mobile browser chrome matches the
 * theme. iOS Safari ignores in-place content changes (it only reads theme-color
 * at load), so we REMOVE and RE-INSERT the element — a fresh node sometimes nudges
 * it to re-read on a theme toggle. Not guaranteed on every iOS version, but it's
 * the best available for a regular browser tab.
 */
export const setThemeColor = (theme: 'dark' | 'light'): void => {
  const colors = THEME_COLORS[currentBrand()];
  const color = colors[theme] ?? colors.dark;
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.remove());
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'theme-color');
  meta.setAttribute('content', color);
  document.head.appendChild(meta);
};

/**
 * Points for one concentric-diamond ring at value v, in the 680-unit viewBox
 * (center 340,340): the ring spans v → 680-v. Shared by the logo mark, the VAE
 * signature mark, and the animated hero signature.
 */
export const diamondPoints = (v: number): string =>
  `340,${v} ${680 - v},340 340,${680 - v} ${v},340`;
